import { GetServerSideProps } from 'next';
import React from 'react';
import { useViewportScroll } from 'framer-motion';

import { useForceUpdateOnWindowResize } from '@zola/zola-ui/src/hooks/useForceUpdateOnWindowResize';
import { UserContext } from '@zola-helpers/client/dist/es/@types';
import useGraphiteRelatedLinks, {
  GraphiteLinkType,
} from '@zola/zola-ui/src/hooks/useGraphiteRelatedLinks';

// Zola UI Components
import { PreauthFooterV3 } from '@zola/zola-ui/src/components/PreAuthV3';
import Differentiation from '@zola/zola-ui/src/components/PreAuthV3/Differentiation';
import SupportAccordion from '@zola/zola-ui/src/components/PreAuthV3/SupportAccordion';

// Utils
import { fetchNavHTML } from 'libs/server/nav';
import { fetchUser } from 'libs/server/users';
import isClientSide from 'libs/client/utils/isClientSide';
import { DeviceProvider } from 'contexts/DeviceContext';

// LP components
import Nav from 'components/Nav';
import Header from 'components/LP/WeddingPlanning/Header';
import Hero from 'components/LP/WeddingPlanning/Hero';
import PlanningGrid from 'components/LP/WeddingPlanning/PlanningGrid';

import { PageWrapper } from 'components/LP/v3/PageWrapper.styles';

// Content
import {
  toolsDifferentiationContent,
  FAQItems,
  reviewItems,
  everythingDifferentiationContent,
} from 'components/LP/WeddingPlanning/content';
import { useIsPostAuthNav3MobileOrDesktop } from 'hooks/useIsPostAuthNav3Active';

export type WeddingPlanningProps = {
  userAgent: string | undefined;
  navHTML: string | null;
  userCtx: UserContext | null;
};

const WeddingPlanning: React.FC<WeddingPlanningProps> = ({
  userAgent,
  navHTML,
  userCtx,
}): JSX.Element | null => {
  useForceUpdateOnWindowResize();
  const isPostAuthNav3Enabled = useIsPostAuthNav3MobileOrDesktop(userCtx);

  const hasWeddingAccount = userCtx && userCtx.has_wedding_account;
  if (hasWeddingAccount && isClientSide()) {
    document.location.href = '/wedding/manage';
  }

  const adviceLinks = useGraphiteRelatedLinks(
    GraphiteLinkType.PREAUTH_TO_EXPERT_ADVICE,
    '/wedding-planning'
  );

  const { scrollYProgress } = useViewportScroll();

  if (hasWeddingAccount) {
    return null;
  }

  return (
    <DeviceProvider userAgent={userAgent}>
      <Header />
      <Nav navHTML={navHTML} showPromo user={userCtx} disableSecondary disablePrimaryNavCollapse />
      <PageWrapper isPostAuthNav3={isPostAuthNav3Enabled}>
        <Hero />
        <PlanningGrid scrollYProgress={scrollYProgress} />
        <Differentiation
          title={{
            children: (
              <>
                Tools that are <span>stress-free,</span> hassle-free, cost-free
              </>
            ),
          }}
          content={toolsDifferentiationContent}
          isPhotoOnRight={false}
        />
        <Differentiation
          title={{
            children: (
              <>
                <span>Everything you need</span> to plan the wedding you want
              </>
            ),
          }}
          content={everythingDifferentiationContent}
        />
        <SupportAccordion
          advice={{ links: adviceLinks }}
          faqs={FAQItems}
          reviews={reviewItems}
          collapsed
        />
        <PreauthFooterV3 isGuest={userCtx ? Boolean(userCtx.is_guest) : true} />
      </PageWrapper>
    </DeviceProvider>
  );
};

export const getServerSideProps: GetServerSideProps<WeddingPlanningProps> = async Ctx => {
  const { req } = Ctx;
  const [navHTML, userCtx] = await Promise.all([fetchNavHTML(), fetchUser(req)]);

  const userAgent = req.headers['user-agent'];

  return {
    props: {
      userAgent,
      navHTML,
      userCtx,
    },
  };
};

export default WeddingPlanning;
